import { 
  Box, 
  Button, 
  Heading, 
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  useToast,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react';
import { 
  FiUsers, 
  FiCalendar, 
  FiDollarSign, 
  FiBarChart, 
  FiHome,
  FiLogOut,
  FiSettings,
  FiUserCheck,
  FiMenu,
  FiMapPin,
  FiFileText,
  FiShare2
} from 'react-icons/fi';
import React from 'react';
import { Link, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import UsersManagement from './components/UsersManagement';
import BookingsManagement from './components/BookingsManagement';
import PaymentManagement from './components/PaymentManagement';
import Analytics from './components/Analytics';
import ServicesManagement from './components/ServicesManagement';
import TechnicianManagement from './components/TechnicianManagement';
import FirestoreTest from './components/FirestoreTest';
import EnvironmentSelector from './components/Common/EnvironmentSelector';
import AdminSettingsHome from './components/AdminSettings/AdminSettingsHome';
import BusinessHoursSettings from './components/AdminSettings/BusinessHours';
import BookingConstraintsSettings from './components/AdminSettings/BookingConstraints';
import PaymentSettings from './components/AdminSettings/PaymentSettings';
import BookingPoolSettings from './components/AdminSettings/BookingPoolSettings';
import ServiceAreaSettings from './components/AdminSettings/ServiceArea';
import HolidayCalendarSettings from './components/AdminSettings/HolidayCalendar';
import GiftCardTemplates from './components/AdminSettings/GiftCardTemplates';
import PromoCodeManagement from './components/AdminSettings/PromoCodeManagement';
import EmailSettings from './components/AdminSettings/EmailSettings';
import PolicySettings from './components/AdminSettings/PolicySettings';
import FAQsManagement from './components/AdminSettings/FAQsManagement';
import VoiceSettings from './components/AdminSettings/VoiceSettings';
import SEOSettings from './components/AdminSettings/SEOSettings';
import NotificationsManagement from './components/Notifications/NotificationsManagement';
import WaitingListManagement from './components/WaitingListManagement';
import CRMManagement from './components/CRMManagement';
import BlogManagement from './components/BlogManagement';
import SocialMediaManagement from './components/SocialMediaManagement';

// Sidebar Content Component (shared between fixed sidebar and drawer)
function SidebarContent({ onClose }: { onClose?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: FiHome },
    { path: '/users', label: 'Users', icon: FiUsers },
    { path: '/technicians', label: 'Technicians', icon: FiUserCheck },
    { path: '/bookings', label: 'Bookings', icon: FiCalendar },
    { path: '/services', label: 'Services', icon: FiSettings },
    { path: '/payments', label: 'Payments', icon: FiDollarSign },
    { path: '/waiting-list', label: 'Waiting List', icon: FiMapPin },
    { path: '/crm', label: 'CRM', icon: FiUsers },
    { path: '/blog', label: 'Blog', icon: FiFileText },
    { path: '/social-media', label: 'Social Media', icon: FiShare2 },
    { path: '/notifications', label: 'Notifications', icon: FiSettings },
    { path: '/analytics', label: 'Analytics', icon: FiBarChart },
    { path: '/settings', label: 'Settings', icon: FiSettings },
    { path: '/firestore-test', label: 'Firestore Test', icon: FiSettings },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: 'Logged out successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/login');
    } catch (error) {
      toast({
        title: 'Logout failed',
        description: 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleLinkClick = () => {
    // Close drawer when a link is clicked (mobile/tablet)
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Only show header content in fixed sidebar (not in drawer) */}
      {!onClose && (
        <Box p={6}>
          <HStack spacing={3} mb={4} align="center">
            <Box
              as="img"
              src="/geppetto-icon-192.png"
              alt="Geppetto"
              w="40px"
              h="40px"
              borderRadius="md"
              flexShrink={0}
            />
            <Heading size="md" color="green.600">Geppetto</Heading>
          </HStack>
          <EnvironmentSelector />
        </Box>
      )}
      
      <VStack spacing={1} align="stretch" px={4} flex={1} overflowY="auto">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} onClick={handleLinkClick}>
              <Button
                w="full"
                justifyContent="flex-start"
                variant={isActive ? "solid" : "ghost"}
                colorScheme={isActive ? "blue" : "gray"}
                leftIcon={React.createElement(item.icon as React.ElementType)}
                mb={1}
                minH="44px" // Ensure touch target size
              >
                {item.label}
              </Button>
            </Link>
          );
        })}
      </VStack>
      
      <Box p={4} borderTop="1px" borderColor={borderColor} mt="auto">
        <Button
          w="full"
          justifyContent="flex-start"
          variant="ghost"
          colorScheme="red"
          leftIcon={<Icon as={FiLogOut as React.ElementType} />}
          onClick={handleLogout}
          minH="44px" // Ensure touch target size
        >
          Sign Out
        </Button>
      </Box>
    </>
  );
}

// Sidebar Component (Fixed for desktop, Drawer for mobile/tablet)
function Sidebar() {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Show fixed sidebar on desktop (lg and above), drawer on tablet/mobile
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  if (isDesktop) {
    // Fixed sidebar for desktop
    return (
      <Box
        w="250px"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        h="100vh"
        position="fixed"
        left={0}
        top={0}
        zIndex={10}
        display="flex"
        flexDirection="column"
      >
        <SidebarContent />
      </Box>
    );
  }

  // Drawer for mobile/tablet
  return (
    <>
      <IconButton
        aria-label="Open menu"
        icon={React.createElement(FiMenu as React.ElementType)}
        onClick={onOpen}
        position="fixed"
        top={4}
        left={4}
        zIndex={20}
        colorScheme="blue"
        size="lg"
        minH="44px"
        minW="44px"
      />
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg={bgColor} maxW="280px">
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px" borderColor={borderColor} pb={4}>
            <HStack spacing={3} mb={4} align="center">
              <Box
                as="img"
                src="/geppetto-icon-192.png"
                alt="Geppetto"
                w="32px"
                h="32px"
                borderRadius="md"
                flexShrink={0}
              />
              <Heading size="md" color="green.600">Geppetto</Heading>
            </HStack>
            <EnvironmentSelector />
          </DrawerHeader>
          <DrawerBody p={0} display="flex" flexDirection="column" overflowY="auto">
            <SidebarContent onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

// Main Layout Component
function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box minH="100vh" bg="gray.50">
      <Sidebar />
      <Box 
        ml={{ base: 0, lg: "250px" }} 
        pt={{ base: "72px", lg: 0 }} // Add top padding on mobile for hamburger menu
        p={{ base: 4, md: 6, lg: 8 }} // Responsive padding
      >
        {children}
      </Box>
    </Box>
  );
}

const AppContent: React.FC = () => {
  return (
    <Router>
          <Routes>
        <Route path="/login" element={<LoginForm />} />
        <Route path="/" element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <DashboardLayout>
              <UsersManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/bookings" element={
          <ProtectedRoute>
            <DashboardLayout>
              <BookingsManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/payments" element={
          <ProtectedRoute>
            <DashboardLayout>
              <PaymentManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/waiting-list" element={
          <ProtectedRoute>
            <DashboardLayout>
              <WaitingListManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/crm" element={
          <ProtectedRoute>
            <DashboardLayout>
              <CRMManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/blog" element={
          <ProtectedRoute>
            <DashboardLayout>
              <BlogManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/social-media" element={
          <ProtectedRoute>
            <DashboardLayout>
              <SocialMediaManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <DashboardLayout>
              <Analytics />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/services" element={
          <ProtectedRoute>
            <DashboardLayout>
              <ServicesManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/technicians" element={
          <ProtectedRoute>
            <DashboardLayout>
              <TechnicianManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <DashboardLayout>
              <NotificationsManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/firestore-test" element={
          <ProtectedRoute>
            <DashboardLayout>
              <FirestoreTest />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdminSettingsHome />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/business-hours" element={
          <ProtectedRoute>
            <DashboardLayout>
              <BusinessHoursSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/booking-constraints" element={
          <ProtectedRoute>
            <DashboardLayout>
              <BookingConstraintsSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/payment" element={
          <ProtectedRoute>
            <DashboardLayout>
              <PaymentSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/booking-pools" element={
          <ProtectedRoute>
            <DashboardLayout>
              <BookingPoolSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/service-area" element={
          <ProtectedRoute>
            <DashboardLayout>
              <ServiceAreaSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/holidays" element={
          <ProtectedRoute>
            <DashboardLayout>
              <HolidayCalendarSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/gift-card-templates" element={
          <ProtectedRoute>
            <DashboardLayout>
              <GiftCardTemplates />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/promo-codes" element={
          <ProtectedRoute>
            <DashboardLayout>
              <PromoCodeManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/email" element={
          <ProtectedRoute>
            <DashboardLayout>
              <EmailSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/policy" element={
          <ProtectedRoute>
            <DashboardLayout>
              <PolicySettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/faqs" element={
          <ProtectedRoute>
            <DashboardLayout>
              <FAQsManagement />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/voice" element={
          <ProtectedRoute>
            <DashboardLayout>
              <VoiceSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/seo" element={
          <ProtectedRoute>
            <DashboardLayout>
              <SEOSettings />
            </DashboardLayout>
          </ProtectedRoute>
        } />
          </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <EnvironmentProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </EnvironmentProvider>
  );
};

export default App;